import { DSButton } from '@elliemae/ds-button';
import { Grid } from '@elliemae/ds-grid';
import { DSModal } from '@elliemae/ds-modal';
import {
  EppsLoanInformation,
  SchemaSearchForm,
  applyClientLookupsToSchema,
  useLoanDataFieldSelector,
  useRawLookupOptions,
  useLookupOptions,
  validateAndSubmitSearchForm,
} from '@frontend/pricing-search';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import {
  calculateGovie,
  getStateCounties,
  getZipLocations,
  handleGovtFeeUpdate,
  initSchemaForm,
} from '../../../../common/services/searchFormServices';
import { useIsZeroBasedPricing } from '../../../../common/utils/customHooks/useIsZeroBasedPricing';
import {
  useLoanInformationSelector,
  usePricingSearchConfiguration,
  useSimpleSearchConfiguration,
  useUserRolesSelector,
} from '../../../../data/useSelectors';
import { isLockPeriodDisabled } from '../../helpers';
import { applySearchFormRules } from './helpers';
import { setLoanInfo } from '../../../components/Form/utils/common';
import { PATH_SEARCH_FOR_PRODUCTS } from '../../../../route/constants';
import Rights from '../../../../common/services/Rights';

const GOVT_PRODUCT_IDS = [
  EppsLoanInformation.StandardProducts.FHA,
  EppsLoanInformation.StandardProducts.VA,
  EppsLoanInformation.StandardProducts.USDA,
];

const getGovtType = (products: number[]) =>
  products?.find((p) => GOVT_PRODUCT_IDS.includes(p)) ?? null;

export const QuickSearch = ({ onSearch }: any = {}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hasInitForm, setHasInitForm] = useState(false);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [financedAmount, setFinancedAmount] = useState<number>(0);
  const isZeroBasedPricing = useIsZeroBasedPricing();
  const allLookups = useRawLookupOptions();
  const govtUpfrontFees = useLookupOptions('fee') as any[];
  const govtUpfrontFeesRef = useRef(govtUpfrontFees);
  govtUpfrontFeesRef.current = govtUpfrontFees;
  const previousGovtTypeRef = useRef<number | null>(null);
  const pendingFeeChangeRef = useRef(false);
  const loanInfo = useLoanInformationSelector() || {};
  const userRoles = useUserRolesSelector();
  const schema = useSimpleSearchConfiguration();
  // the same channel resolved schema the search form renders from
  const searchFormSchema = usePricingSearchConfiguration();
  const requestLockStatus = useLoanDataFieldSelector('requestLockStatus');
  const lockPeriodDisabled = isLockPeriodDisabled(requestLockStatus);
  const schemaWithLookups = useMemo(() => {
    if (!schema) return schema;
    // Lock period aliasing: show the aliased label unless the user may see the
    // true investor values.
    return applyClientLookupsToSchema(
      applySearchFormRules(schema, searchFormSchema, lockPeriodDisabled),
      allLookups,
      !Rights.viewTrueInvestorName,
    );
  }, [schema, searchFormSchema, lockPeriodDisabled, allLookups]);
  const loanTermsLookups = useLookupOptions('loanTerms');

  const schemaForm = useForm<any>({
    mode: 'onSubmit',
    defaultValues: {},
    resetOptions: { keepValues: false },
  });

  useEffect(() => {
    initSchemaForm(schemaForm, {
      loanInfo,
      roleName: userRoles?.roleName || '',
      allLookups,
      canAssignLoanOfficer: userRoles?.canAssignLoanOfficer ?? true,
      viewDeliveryAndCommitment: userRoles?.viewDeliveryAndCommitment ?? true,
    });
    previousGovtTypeRef.current = getGovtType(loanInfo?.standardProducts || []);
    setHasInitForm(true);
  }, [
    loanInfo,
    userRoles?.roleName,
    allLookups,
    userRoles?.canAssignLoanOfficer,
    userRoles?.viewDeliveryAndCommitment,
  ]);

  useEffect(() => {
    const subscription = schemaForm.watch(async (values, { name, type }) => {
      if (!name || type !== 'change') return;

      const currentGovtType = getGovtType(values.standardProducts || []);
      const prevGovtType = previousGovtTypeRef.current;

      if (
        prevGovtType !== null &&
        currentGovtType !== null &&
        currentGovtType !== prevGovtType
      ) {
        schemaForm.setValue('loanInformation.feeFactor', undefined);
        schemaForm.setValue('loanInformation.govtFeeFactor', undefined);
        schemaForm.setValue('loanInformation.feeAmount', 0);
        schemaForm.setValue('loanInformation.feeAmountFinanced', 0);
        schemaForm.setValue('loanInformation.feeAmountPaidinCash', 0);
        schemaForm.setValue('loanInformation.scenario', 'FULLY_FINANCED');
        pendingFeeChangeRef.current = true;
        previousGovtTypeRef.current = currentGovtType;
      } else if (currentGovtType !== null) {
        if (currentGovtType !== prevGovtType) {
          previousGovtTypeRef.current = currentGovtType;
        }
        pendingFeeChangeRef.current = false;
      }

      await handleGovtFeeUpdate(schemaForm, {
        govtUpfrontFees: govtUpfrontFeesRef.current,
      });
    });
    return () => subscription.unsubscribe();
  }, [schemaForm]);

  useEffect(() => {
    return () => {
      const { nonLoanValues, ...loanValues } = schemaForm.getValues();
      dispatch(setLoanInfo(loanValues) as any);
    };
  }, [schemaForm, dispatch]);

  const standardProducts =
    useWatch({ control: schemaForm.control, name: 'standardProducts' }) ?? [];

  const hasMultipleGovLoans =
    GOVT_PRODUCT_IDS.filter((id) => standardProducts.includes(id)).length >= 2;

  const handleQualify = async () => {
    if (pendingFeeChangeRef.current) {
      const result = await handleGovtFeeUpdate(schemaForm, {
        govtUpfrontFees: govtUpfrontFeesRef.current,
      });
      const amt =
        result?.feeAmountFinanced ??
        schemaForm.getValues('loanInformation.feeAmountFinanced') ??
        0;
      setFinancedAmount(amt);
      setShowFeeModal(true);
      return;
    }
    const result = await validateAndSubmitSearchForm({
      schema: schemaWithLookups,
      schemaForm,
      preview: false,
      loanTerms: loanTermsLookups,
    });

    if (!result.valid) return;
    await schemaForm.trigger();
    onSearch(schemaForm.getValues(), false);
  };

  return (
    <div>
      <Grid
        cols={['auto', '80px']}
        gutter="xs"
        marginBottom="15px"
        marginTop="15px"
        data-testid="search-results-quick-search"
      >
        {!hasInitForm || !schemaWithLookups ? null : (
          <Grid>
            <SchemaSearchForm
              schema={schemaWithLookups}
              schemaForm={schemaForm}
              preview={false}
              getStateCounties={getStateCounties}
              getZipLocations={getZipLocations}
              calculateGovie={calculateGovie}
              activeAccordions={{}}
              setActiveAccordions={() => {}}
              isZeroBasedPricing={isZeroBasedPricing}
            />
          </Grid>
        )}
        <Grid style={{ marginTop: '55px' }}>
          <DSButton
            data-testid="quick-search-search-button"
            buttonType="secondary"
            labelText="Qualify"
            style={{ width: '100%' }}
            onClick={handleQualify}
            disabled={hasMultipleGovLoans}
          />
        </Grid>
      </Grid>
      <DSModal
        isOpen={showFeeModal}
        onClose={() => {
          setShowFeeModal(false);
          pendingFeeChangeRef.current = false;
        }}
        onConfirm={() =>
          navigate(PATH_SEARCH_FOR_PRODUCTS, {
            state: { promptFeeReview: true },
          })
        }
        onReject={() => {
          setShowFeeModal(false);
          pendingFeeChangeRef.current = false;
        }}
        confirmLabel="Update Fee Factor"
        rejectLabel="Okay"
        showRejectButton
        shouldCloseOnOverlayClick
        modalTitle="Fee Factor Changed"
        dataTestId="quick-search-fee-changed-modal"
      >
        <p>
          Your program selections could change your fee factors. Prior to
          selection, validate your fee factor applies to the selected program.
          Financed amount is now ${financedAmount.toLocaleString()}.
        </p>
      </DSModal>
    </div>
  );
};
